import type { ApolloQueryResult } from '@apollo/client'
import type { IssueQuery } from '..'

type Options = {
  maxRetries?: number
  delayMs?: number
  /** Return false to abort the poll (e.g. component unmounted). */
  isActive?: () => boolean
}

/**
 * Export is async (Celery): the EXPORTED action log is written by the worker
 * after the export mutation returns. Poll the issue query until the action-log
 * count grows past the baseline.
 *
 * Sequential by design — never starts a refetch until the previous one
 * resolves, and only waits between attempts.
 */
export async function pollForNewActionLog(
  refetch: () => Promise<ApolloQueryResult<IssueQuery>>,
  onData: (data?: IssueQuery) => void,
  baselineCount: number,
  { maxRetries = 3, delayMs = 2000, isActive }: Options = {}
) {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    if (isActive && !isActive()) return

    try {
      const { data } = await refetch()
      if (isActive && !isActive()) return
      onData(data)

      const count = data?.issue?.actionLogs?.length ?? 0
      if (count > baselineCount) return
    } catch {
      // Transient refetch error — fall through to retry / give up.
    }

    if (attempt < maxRetries - 1) {
      await new Promise<void>(resolve => {
        setTimeout(resolve, delayMs)
      })
    }
  }
}
